// Weekly review sections (spec §8.5). Pure.
import type { TaskResource } from '@/kinds/task/apply'
import { isTemplate } from '@/kinds/task/schema'
import type { ProjectResource } from '@/kinds/project/apply'
import type { SettingsSpec } from '@/kinds/settings'
import { hoursAgo } from './time'

export type ReviewSection = 'slipped' | 'stale_inbox' | 'stale_next' | 'waiting' | 'projects'

export interface ReviewPlan {
  slipped: TaskResource[]
  stale_inbox: TaskResource[]
  stale_next: TaskResource[]
  waiting: TaskResource[]
  projects: ProjectResource[]
  total: number
}

export const planReview = (
  tasks: TaskResource[],
  projects: ProjectResource[],
  settings: SettingsSpec,
  today: string,
  now = new Date(),
): ReviewPlan => {
  const open = tasks.filter(
    (t) => !t.deleted_at && !isTemplate(t.spec) && t.status.state !== 'done' && t.status.state !== 'dropped',
  )
  const slipped = open.filter((t) => !!t.spec.due && t.spec.due < today)
  const stale_inbox = open.filter(
    (t) =>
      t.status.state === 'inbox' && hoursAgo(t.status.last_touched, now) > settings.stale_inbox_days * 24,
  )
  const stale_next = open.filter(
    (t) =>
      t.status.state === 'next' &&
      !t.spec.scheduled &&
      hoursAgo(t.status.last_touched, now) > settings.stale_next_days * 24,
  )
  const waiting = open.filter((t) => t.status.state === 'waiting')
  const active = projects.filter(
    (p) => !p.deleted_at && p.status.state === 'active' && (p.status.open_tasks ?? 0) === 0,
  )
  return {
    slipped,
    stale_inbox,
    stale_next,
    waiting,
    projects: active,
    total: slipped.length + stale_inbox.length + stale_next.length + waiting.length + active.length,
  }
}

/** Completions in the last 7 days (the only stat). */
export const completionsThisWeek = (tasks: TaskResource[], now = new Date()): TaskResource[] => {
  const cutoff = new Date(now.getTime() - 7 * 86_400_000).toISOString()
  return tasks.filter(
    (t) => t.status.state === 'done' && !!t.status.completed_at && t.status.completed_at >= cutoff,
  )
}
